/**
 * Password security helpers — length validation + Have I Been Pwned breach check.
 *
 * Rationale (NIST SP 800-63B / NCSC password guidance):
 *  - Length is what matters; complexity rules push users to predictable
 *    patterns ("Password1!") and into reuse. We require 12 characters,
 *    no other rules.
 *  - We additionally block any password known to have appeared in a
 *    data breach via the HIBP k-anonymity API. Only the first 5 hex
 *    chars of the SHA-1 hash leave the device; the password itself
 *    never does.
 *  - The 12-char minimum is also configured server-side in Supabase
 *    Auth so this floor still holds if the client validation is bypassed.
 */

const MIN_PASSWORD_LENGTH = 12;

/**
 * Validate password against our local rules (length only — no complexity
 * rules per NIST guidance).
 *
 * Returns null if valid, or a human-readable error message if not.
 */
export function validatePasswordRules(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters. Length is more important than mixing in symbols — a memorable passphrase like four random words is ideal.`;
  }
  return null;
}

/**
 * Check whether a password has appeared in any known data breach via
 * the Have I Been Pwned k-anonymity API.
 *
 * Privacy model: we SHA-1 the password client-side, send ONLY the first
 * 5 hex characters of the hash (~1 in 1,048,576 collision rate) to HIBP's
 * range endpoint, then look for our suffix in the returned list locally.
 * The plaintext password and the full hash never leave the device.
 *
 * Returns:
 *  - The number of times the password has appeared in breaches (0 = clean).
 *
 * Failure mode: if the API is unreachable (offline, blocked, rate-limited)
 * we fail-OPEN — return 0 rather than block signup. The 12-char floor
 * still applies. Logging the warning lets us spot persistent issues.
 */
export async function checkPasswordBreached(password: string): Promise<number> {
  try {
    // SHA-1 hash the password client-side
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    // k-anonymity: only send the 5-char prefix. The API returns all known
    // breached hashes with that prefix and their seen-count.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!res.ok) {
      console.warn('[HIBP] API returned', res.status);
      return 0;
    }

    const text = await res.text();

    // Response format: each line is "SUFFIX:COUNT"
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const [s, count] = trimmed.split(':');
      if (s === suffix) {
        const n = parseInt(count || '0', 10);
        return Number.isFinite(n) ? n : 0;
      }
    }
    return 0;
  } catch (e) {
    // Network error, offline, or aborted — fail open. Don't block signup
    // just because HIBP is down or the user is on a flaky site connection.
    console.warn('[HIBP] check failed; failing open', e);
    return 0;
  }
}

/**
 * Convenience wrapper: run both local rule check AND breach check.
 * Returns an error message if invalid, null if valid.
 *
 * Use this from signup, reset-password, and accept-invite flows.
 * Caller is responsible for showing a loading indicator — the HIBP
 * round-trip typically takes 100-400ms.
 */
export async function validatePassword(password: string): Promise<string | null> {
  const ruleError = validatePasswordRules(password);
  if (ruleError) return ruleError;

  const breachCount = await checkPasswordBreached(password);
  if (breachCount > 0) {
    return `This password has appeared in ${breachCount.toLocaleString()} known data breach${breachCount === 1 ? '' : 'es'}. Please choose a different one — attackers try leaked passwords first.`;
  }
  return null;
}
